import { AppDispatch, store } from "../../store";
import { authActions } from "../../modules/auth";
import { helpers } from "../../packages/helpers";
import { handleMessage } from "./handle-message";
import { RECONNECT_TIMEOUTS } from "./constants";
import type { ControlState } from "../../modules/control";

const { auth } = authActions;
const { asyncEmpty } = helpers.utilities;

export class SSE {
  private isActive = false;

  private authApi: ControlState["api"]["authApi"] = asyncEmpty;

  private dispatch: AppDispatch = store.dispatch;

  private eventSource?: EventSource;

  private lastEventId: string | null = null;

  private url = {
    default: "",
    path: "",
    isSet: false,
  };

  private reconnect = {
    timeout: 0,
  };

  private resetReconnectTimeout() {
    if (this.reconnect.timeout) {
      this.reconnect.timeout = 0;
    }
  }

  private setLastEventId(event: MessageEvent) {
    const isLastEventIdValid = event.lastEventId && event.lastEventId !== "0";

    if (isLastEventIdValid) {
      this.lastEventId = event.lastEventId;
    }
  }

  private handleOpenSSE() {
    this.setActive();
    this.resetReconnectTimeout();
  }

  setAuth(authApi: ControlState["api"]["authApi"], urlPath: string) {
    this.authApi = authApi;
    this.url.default = urlPath;
  }

  setData(userId: string, accessToken: string) {
    this.url.path = this.url.default
      .replace("{{userId}}", userId)
      .replace("{{accessToken}}", accessToken);

    this.url.isSet = true;
  }

  private isUrlSet() {
    return this.url.isSet;
  }

  getIsActive() {
    return this.isActive;
  }

  private setActive() {
    this.isActive = true;
  }

  private setNotActive() {
    this.isActive = false;
  }

  private handleMessageSSE(event: MessageEvent) {
    handleMessage(store)(event);

    this.setLastEventId(event);
    this.resetReconnectTimeout();
  }

  private async handleErrorSSE() {
    if (this.eventSource) {
      window.removeEventListener("beforeunload", this.eventSource.close);
      this.eventSource?.close();
      this.setNotActive();
    }

    const { authApi } = this;
    const { timeout } = this.reconnect;

    setTimeout(async () => {
      if (RECONNECT_TIMEOUTS[timeout]) {
        this.reconnect.timeout = RECONNECT_TIMEOUTS[timeout];
      }

      await this.dispatch(auth({ authApi }));
      await this.connect();
    }, timeout);
  }

  async connect() {
    if (!this.isUrlSet()) {
      return;
    }

    if (this.eventSource) {
      window.removeEventListener("beforeunload", this.eventSource.close);
      this.eventSource?.close();
    }

    const lastEventParam = this.lastEventId
      ? "&lastEventId=" + this.lastEventId
      : "";

    const url = this.url.path + lastEventParam;

    this.eventSource = new EventSource(url);
    this.eventSource.onopen = this.handleOpenSSE.bind(this);
    this.eventSource.onmessage = this.handleMessageSSE.bind(this);
    this.eventSource.onerror = this.handleErrorSSE.bind(this);
    this.setActive();

    if (this.eventSource) {
      window.addEventListener("beforeunload", this.eventSource.close);
    }
  }

  disconnect() {
    if (!this.eventSource) {
      return;
    }

    this.eventSource.close();
    this.setNotActive();
    this.resetReconnectTimeout();
  }
}
